import axios from 'axios';
import { useToast } from '@/composables/use-toast.js';
import { logApiError } from '@/services/api.js';
import { useStorage } from '@/composables/use-storage.js';
import { useEvals } from '@/composables/use-evals.js';
import { prepareEvalBuilderFiles } from '@/pages/dataset/services/eval/evalBuilderFileService';
import { createDataset } from '@/services/api';
import { EVAL_TYPES } from '@/pages/eval/eval-builder/constants/evalTypes';
const { addToast } = useToast();
const { userAuthToken } = useStorage();
const { clearPreviewEvalPayload } = useEvals();

const evalBaseUrl = '/api/evals';
const baseURL = process.env.VUE_APP_BACKEND_URL || 'http://localhost:3000';

const getAuthHeaders = () => {
    const token = userAuthToken.value;
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      withCredentials: true
    };
  };

export const createEval = async (values = {}) => {
    try {
        const payload = {
            created_at: values?.created_at || new Date().toISOString(),
            ...values,
            eval_type: values?.eval_type || EVAL_TYPES.QA
        };
        const response = await axios.post(`${evalBaseUrl}`, payload);
        return response.data;
    } catch (error) {
        logApiError(error, 'POST', `${evalBaseUrl}`);

        addToast('Error fetching draft listing', 'error');
        return false;
    }
}

const getUploadHeaders = () => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Accept': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };
  
const initiateUpload = async (headers, fileName, fileSize, ownerId, evalId, evalType = EVAL_TYPES.QA) => {
    const updateEvalUrl = `${baseURL}${evalBaseUrl}`;
    const startResponse = await fetch(`${updateEvalUrl}/upload/start`, {
        method: 'POST',
        credentials: 'include',
        headers: {
        ...headers,
        'Content-Type': 'application/json'
        },
        body: 
        JSON.stringify({
            fileInfos: {
                name: fileName,
                size: fileSize,
                ownerId: Number(ownerId),
            },
            evalId: evalId,
            eval_type: evalType
        })
    });

    if (!startResponse.ok) {
        const errorData = await startResponse.json();
        if (startResponse.status === 409) {
        addToast(errorData.message, 'error');
        return { error: 'FILE_EXISTS', message: errorData.message };
        }
        addToast(errorData.message, 'error');
        throw new Error('Failed to start upload');
    }

    const startResponseData = await startResponse.json();
    if (startResponseData.status !== 201) {
        return { error: startResponseData.message };
    }

    return startResponseData;
};

const parseFileName = (originalName) => {
const parts = originalName.split('.');
const fileName = parts[0];
const fileExtension = parts.length > 2 ? `${parts[1]}.${parts[2]}` : parts[1];
return { fileName, fileExtension };
};

const uploadChunk = async (baseURL, headers, chunk, uploadData) => {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('uploadId', uploadData.uploadId);
    formData.append('chunkNumber', uploadData.chunkNumber.toString());
    formData.append('totalChunks', uploadData.totalChunks.toString());
    formData.append('filename', uploadData.fileName);
    formData.append('totalSize', uploadData.totalSize.toString());
    formData.append('fileType', uploadData.fileExtension);

    const chunkResponse = await fetch(`${baseURL}/upload/chunk`, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: formData
    });

    if (!chunkResponse.ok) {
        throw new Error(`Error sending chunk ${uploadData.chunkNumber}`);
    }
};

const sendChunks = async (headers, file, ownerId, onProgress, evalId, evalType = EVAL_TYPES.QA) => {
    const chunkMaxSize = 14;
    const chunkSize = chunkMaxSize * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    const finalFileName = file.name;
    
    const startResult = await initiateUpload(headers, finalFileName, file.size, ownerId, evalId, evalType);
    if (startResult.error) return startResult;
    const { fileName, fileExtension } = parseFileName(finalFileName);
    
    for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        const updateEvalUrl = `${baseURL}${evalBaseUrl}`;
        await uploadChunk(updateEvalUrl, headers, chunk, {
            uploadId: startResult.uploadId,
            chunkNumber: i + 1,
            totalChunks,
            fileName,
            totalSize: file.size,
            fileExtension,
            evalId
        });

        if (onProgress) {
            onProgress(((i + 1) / totalChunks) * 100);
        }
    }

}

export const uploadEvalFile = async (files, ownerId, onProgress, evalId = null, evalType = EVAL_TYPES.QA) => {
    const headers = getUploadHeaders();
    const createdEval = evalId ?? await createEval({ eval_type: evalType });
    const newEvalId = evalId ?? createdEval?.id;
    if (newEvalId == null) return;
    
    try {
        for (const file of files) {
            if(file) {
                await sendChunks(headers, file, ownerId, onProgress, newEvalId, evalType)
            }
        }
    } catch (error) {
      console.error(error);
      throw error;
    }
    return newEvalId;
};

export const fetchTaskList = async (datasetId) => {
    try {
    const response = await axios.get(`/api/eval-runs/${datasetId}/task-list`, getAuthHeaders());
    return response.data;
    } catch (error) {
      const status = error.response?.status ?? error.status;
      if (status === 403) {
        return { accessDenied: true };
      }
      logApiError(error, 'GET', `/api/eval-runs/${datasetId}/task-list`);
      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Error fetching task list file';
      addToast(errorMessage, 'error');
      return false;
    }
  };

  export const uploadModelResponseFile = async (datasetId, file, version = null) => {
    try {
        const headers = {
            'Content-Type': 'multipart/form-data',
            'Accept': 'application/json'
        }
        const response = await axios.post(`/api/eval-runs/${datasetId}/${version}/upload`, file, {
            headers,
        });
        return response;
    } catch (error) {
      logApiError(error, 'POST', `/api/eval-runs/${datasetId}/upload`);
      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Error uploading model response file';
      addToast(errorMessage, 'error');
      return false;
    }
  }
  
  export const downloadTaskBundlesFile = async (datasetId) => {
    try {
      const baseURL = axios.defaults.baseURL || 'http://localhost:3000';
      const token = localStorage.getItem('authToken');
      const headers = {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
      };

      if (token) {
          headers['Authorization'] = `Bearer ${token}`;
      }

      const url = `${baseURL}/api/eval-runs/${datasetId}/download-task-bundles-file`;

      const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers
      });

      if (!response.ok) {
          const error = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(error.message || 'Download failed');
      }

      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;
      let done = false;

      while (!done) {
          const result = await reader.read();
          done = result.done;

          if (!done) {
              chunks.push(result.value);
              receivedLength += result.value.length;
          }
      }

      const allChunks = new Uint8Array(receivedLength);
      let position = 0;

      for (const chunk of chunks) {
          allChunks.set(chunk, position);
          position += chunk.length;
      }

      const filename = `task-bundles-${datasetId}.tar.gz`;
      const blob = new Blob([allChunks], { type: 'application/gzip' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
      return true;
    } catch (error) {
      logApiError(error, 'GET', `/api/eval-runs/${datasetId}/download-task-bundles-file`);
      return false;
    }
  };
  
  export const createNewEvalRun = async (evalRunData) => {
    try {
      const response = await axios.post('/api/eval-runs', evalRunData, getAuthHeaders());
      return response.data;
    } catch (error) {
      logApiError(error, 'POST', '/api/eval-runs');
      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Error creating eval run';
      addToast(errorMessage, 'error');
      return false;
    }
  }

  export const downloadEvalReport = async (evalRunId, mode = '', listingName = '') => {
    try {
        if (mode === 'file') {
            const response = await axios.get(`/api/eval-runs/${evalRunId}/download-report/${mode}` , {
                ...getAuthHeaders(),
                responseType: 'blob',
                transformResponse: [(data) => data]
            });
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^";]+)"?/);
            const filename = match ? match[1] : `eval_report_${listingName}.json`;
            return { blob: response.data, filename };
        }
        const response = await axios.get(`/api/eval-runs/${evalRunId}/download-report/${mode}`, getAuthHeaders());
        return response.data;
    } catch (error) {
        logApiError(error, 'GET', `/api/eval-runs/${evalRunId}/download-report/${mode}`);
        const errorMessage = error.response?.data?.message ||
            error.message ||
            'Error downloading eval report';
        addToast(errorMessage, 'error');
        return false;
    }
  }

  export const createEvalDatasetFromBuilder = async ({
    datasetName,
    description,
    ownerId,
    tasks,
    onProgress
}) => {
    const preparedData = await prepareEvalBuilderFiles(tasks);

    if (preparedData.errors && preparedData.errors.length > 0) {
        throw new Error(preparedData.errors[0]);
    }

    if (!preparedData.files || preparedData.files.length === 0) {
        throw new Error('No files generated from builder tasks.');
    }

    const evalId = await uploadEvalFile(
        preparedData.files,
        ownerId,
        onProgress
    );

    if (!evalId) {
        throw new Error('Failed to upload eval files.');
    }

    const datasetPayload = {
        dataset: {
            name: datasetName,
            description: description || 'Created via Eval Builder',
            owner_user_id: ownerId,
            eval_id: evalId,
            file_name: null, 
            file_size: 0,
            file_extension: null,
            schema: null,
        },
        comment: 'Initial dataset creation via Builder'
    };

    const response = await createDataset(datasetPayload);

    clearPreviewEvalPayload();

    return response.data;
};

export const processEvalBuilderSubmission = async ({ 
  evalName, 
  selectedOwner, 
  tasks, 
  evalType = EVAL_TYPES.QA,
  coreDatasetInfo,
  onProgress 
}) => {
  try {
      const preparationResult = await prepareEvalBuilderFiles(tasks, null, evalType);

      if (preparationResult.errors && preparationResult.errors.length > 0) {
          return { 
              success: false, 
              error: preparationResult.errors[0] 
          };
      }

      if (!preparationResult.files || preparationResult.files.length === 0) {
          return { 
              success: false, 
              error: 'No files generated to upload.' 
          };
      }

      const evalId = await uploadEvalFile(
          preparationResult.files, 
          selectedOwner, 
          onProgress,
          null,
          evalType
      );

      if (!evalId) {
          return { success: false, error: 'Upload failed during chunk transfer.' };
      }

      const datasetPayload = {
          dataset: {
              name: evalName,
              description: 'Created via Eval Builder',
              owner_user_id: selectedOwner,
              eval_id: evalId,
              file_name: coreDatasetInfo?.file_name || "",
              file_size: coreDatasetInfo?.file_size || 0,
              file_extension: coreDatasetInfo?.file_extension || "",
              schema: null,
              external_source_id: null
          },
          comment: 'Initial dataset creation via Eval Builder'
      };

      const response = await createDataset(datasetPayload);

      if (response.status === 200 || response.status === 201) {
          clearPreviewEvalPayload();
          return { 
              success: true, 
              data: response.data 
          };
      } else {
          return { 
              success: false, 
              error: response.message || 'Failed to create dataset record.' 
          };
      }

  } catch (error) {
      return { 
          success: false, 
          error: error.message || 'Unexpected error during submission.' 
      };
  }
};
